import { actionTree } from "typed-vuex";
import { ActionContext } from "vuex";
import { Response, ResponseError } from "@wl/types/lib/responses";
import { ActionDefinition } from "@wl/types/lib/types/actions/definition";
import { ActiveRes } from "@wl/types/lib/responses/actions/active.res";
import { HistoryRes } from "@wl/types/lib/responses/actions/history.res";
import { RootState } from "..";
import getters from "./getters";
import mutations from "./mutations";
import state, { ActionState } from "./state";

type Active = Response<ActiveRes>;
type History = Response<HistoryRes>;
type Context = ActionContext<ActionState, RootState>;

/**
 * Actions module actions.
 */
const actions = actionTree(
    { state, getters, mutations },
    {
        /**
         * Fetches all module data.
         *
         * @param {Context} _ Action context
         * @return {Promise<void>}
         */
        async fetch(_: Context): Promise<void> {
            await this.app.$accessor.action.getActive();
            await this.app.$accessor.action.getHistory();
        },
        /**
         * Fetches active action definitions.
         *
         * @param {Context} _ Action context
         * @return {(Promise<ResponseError | undefined>)} Potential error
         */
        async getActive(_: Context): Promise<ResponseError | undefined> {
            const response = await this.$axios.get<Active>(`/actions/active?locale=${navigator.language}`);
            if (response.data.error) return response.data.error;

            this.app.$accessor.action.setActive(response.data.data?.definitions);
        },

        /**
         * Fetches the action definition history.
         *
         * @param {Context} _ Action context
         * @return {(Promise<ResponseError | undefined>)} Potential error
         */
        async getHistory(_: Context): Promise<ResponseError | undefined> {
            const response = await this.$axios.get<History>(`/actions/history?locale=${navigator.language}`);
            if (response.data.error) return response.data.error;

            this.app.$accessor.action.setHistory(response.data.data?.history);
        },

        /**
         * Rewards a given action definition.
         *
         * @param {Context} _ Action context
         * @param {ActionDefinition} def Action definition to reward
         * @return {(Promise<ResponseError | undefined>)} Potential error
         */
        async reward(_: Context, def: ActionDefinition): Promise<ResponseError | undefined> {
            const url = def.key ? `/actions/${def.id}/reward?key=${def.key}` : `/actions/${def.id}/reward`;
            const response = await this.$axios.patch(url);
            if (response.data.error) return response.data.error;

            await this.app.$accessor.fetch();
        },
    },
);

export default actions;
